<template>
  <div v-if="!loading" class="product">
    <PointsBanner />
    <div class="product-grid">
      <div class="product__media">
        <ProductMediaGallery />
      </div>
      <div ref="product-details" class="product__info">
        <div class="product__info-inner">
          <div class="product__title-wrapper">
            <div class="product__title-row">
              <h1 class="product__title">{{ product.title.toUpperCase() }}</h1>
              <WishlistButton
                :product="product"
                :variant="variant"
                use-black-icon
                inline
                class="product__title-bookmark"
              />
            </div>
            <div v-if="!isGiftCard" class="product__price">
              <ProductPrice show-percent />
            </div>
            <PreorderDispatchLine
              v-if="showSellingPlanDescription"
              :dispatch-date="preorderDispatchDate"
              :fallback-text="preorderDispatchFallback"
            />
            <ColourFamilySwatches />
          </div>
          <div class="product__sticky-cta">
            <Variants
              v-if="showVariants && !isPreLaunch"
              ref="variants"
              class="variants-component"
            />
            <LowStockMessage v-if="!showStockNotificationForm" />
            <div ref="addToBag" class="product__payment">
              <StockNotificationForm v-if="showStockNotificationForm" />
              <div v-else class="product__payment-row">
                <AddToBag @select-size="scrollToVariants" />
                <DynamicCheckoutButtons v-if="expressCheckoutEnabled" />
              </div>
            </div>
          </div>
          <div class="grid">
            <div class="main">
              <ProductDetails
                show-read-more
                :read-more-limit="3"
                :read-more-toggle-text="{
                  readMore: 'More',
                  readLess: 'Less',
                }"
              />
              <div class="product__advisory">
                <div v-if="isOneSizeProduct" class="product__advisory-item">
                  <img
                    :src="oneSizeIcon"
                    alt="One size"
                    class="product__advisory-icon"
                  />
                  <p>One Size</p>
                </div>
                <div
                  v-if="trueToSizeMessage && !isOneSizeProduct"
                  class="product__advisory-item"
                >
                  <img
                    :src="trueToSizeIcon"
                    alt="True to size"
                    class="product__advisory-icon"
                  />
                  <p>{{ trueToSizeMessage }}</p>
                </div>
                <div v-if="sheernessMessage" class="product__advisory-item">
                  <img
                    :src="sheernessIcon"
                    alt="Sheerness"
                    class="product__advisory-icon"
                  />
                  <p>{{ sheernessMessage }}</p>
                </div>
                <div
                  v-if="showAdvisoryReturnsMessage"
                  class="product__advisory-item"
                >
                  <img
                    :src="exchangesIcon"
                    alt="Exchanges"
                    class="product__advisory-icon"
                  />
                  <p>{{ advisoryReturnsMessage }}</p>
                </div>
              </div>
              <div class="product__bnpl">
                <BNPLInformation :show-icon="false" />
              </div>
              <div class="product__accordion">
                <AccordionGroup is-closed-by-default>
                  <Accordion key="fit-size" title="Fit & Size">
                    <div class="product__fit-size">
                      <p
                        v-if="trueToSizeMessage"
                        class="product__fit-size-copy"
                      >
                        {{ trueToSizeMessage }}
                      </p>
                      <div class="product__fit-size-links">
                        <button
                          type="button"
                          class="product__fit-size-link"
                          @click="openSizeGuide"
                        >
                          View our Size Guide
                        </button>
                        <Easysize
                          v-if="showFitQuizButton"
                          class="product__fit-size-easysize"
                          text=""
                          underline-text="Find My Size"
                          compact
                        />
                      </div>
                    </div>
                  </Accordion>
                  <Accordion
                    v-if="product?.fabricAndPrintDescription"
                    key="fabric"
                    title="Fabric & Feel"
                  >
                    <FabricAndPrint />
                    <!-- <a
                      href="#"
                      class="product__fabric-feel-link"
                      @click.prevent
                    >
                      Learn about our materials
                    </a> -->
                  </Accordion>
                  <Accordion key="aftercare" title="Care">
                    <Aftercare />
                    <!-- <a
                      href="#"
                      class="product__fabric-feel-link"
                      @click.prevent
                    >
                      Read our aftercare guide
                    </a> -->
                  </Accordion>
                  <Accordion key="shipping" title="Delivery & Returns">
                    <ShippingAndReturns />
                    <a
                      href="https://help-gb.ratandboa.com/en-US"
                      class="product__fabric-feel-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      See full returns policy
                    </a>
                  </Accordion>
                </AccordionGroup>
              </div>
              <div class="purchase-info">
                <IconText
                  v-if="showDeliveryEstimateComponent"
                  icon="exclaimation-circle"
                  class="enhanced-edd"
                  use-background
                >
                  <DeliveryEstimatorNoInput
                    class="estimate-delivery-date"
                    show-countdown
                  />
                </IconText>
                <IconText
                  v-if="showPointsBanner"
                  icon="basket-plus"
                  use-background
                >
                  <p>
                    You have {{ creditBalance }} ({{ pointsBalance }} points) to
                    use at Checkout.
                  </p>
                </IconText>
                <!-- <IconText v-if="!isJewellery" icon="hanger" use-background>
                  <Easysize
                    text="Not sure about your size?"
                    underline-text="Try our Fit Quiz."
                  />
                </IconText> -->
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <CompleteTheSet />

    <HowToWear />

    <ProductRecommendationsToggle
      :recommendations="nostoProductRecommendationsProduct"
      :placement="nostoProductRecommendationsProductId"
    />

    <SizeGuideSS25 />
    <PreorderVariantModal />
    <WaitlistDrawer />
  </div>

  <ProductSkeleton v-else />

  <StickyAddToBag
    v-if="!loading && !mq.lgPlus && !isPreLaunch"
    :visible="showStickyBar"
    @select-size="scrollToVariants"
  />
</template>

<script>
// Import components
import HowToWear from "@/components/product-display/HowToWearV2.vue";
import Variants from "@/components/product-display/VariantsV2.vue";
import AddToBag from "@/components/product-display/AddToBagV3.vue";
import DynamicCheckoutButtons from "@/components/product-display/DynamicCheckoutButtonsV2.vue";
import StickyAddToBag from "@/components/product-display/StickyAddToBagV2.vue";
import IconText from "@/components/IconTextV2.vue";
import DeliveryEstimatorNoInput from "@/components/product-display/DeliveryEstimatorNoInputV3.vue";
import BNPLInformation from "@/components/product-display/BNPLInformationV2.vue";
import Easysize from "@/components/product-display/Easysize.vue";
import ProductMediaGallery from "@/components/product-display/ProductMediaGalleryV2.vue";
import AccordionGroup from "@/components/AccordionGroup.vue";
import Accordion from "@/components/AccordionV2.vue";
import ProductDetails from "@/components/product-display/ProductDetails.vue";
import WishlistButton from "@/components/WishlistButtonV3.vue";
import ProductPrice from "@/components/product-display/ProductPrice.vue";
import ShippingAndReturns from "@/components/product-display/ShippingAndReturnsV2.vue";
import ProductSkeleton from "@/components/skeleton/ProductSkeletonV2.vue";
import Aftercare from "@/components/product-display/AftercareV2.vue";
import FabricAndPrint from "@/components/product-display/FabricAndPrint.vue";
import ProductRecommendationsToggle from "@/components/product-display/ProductRecommendationsToggle.vue";
import CompleteTheSet from "@/components/product-display/CompleteTheSetV2.vue";
import StockNotificationForm from "@/components/StockNotificationCompact.vue";
import PointsBanner from "@/components/PointsBanner.vue";
import trueToSizeIcon from "@/assets/icons/true-to-size.png";
import sheernessIcon from "@/assets/icons/sheerness.png";
import exchangesIcon from "@/assets/icons/exchanges.png";
import oneSizeIcon from "@/assets/icons/new-ruler.svg";

// Import internal modules
import productQuery from "@/graphql/queries/ProductV2.gql";
import { regionFeatureToggles } from "@/config.js";
import { getFeatureToggles } from "@/helpers/featureToggles.js";
import { getParams } from "@/helpers/url.js";
import { viewProductEvent, checkProductPages } from "@/helpers/events.js";
import {
  getPreorderDispatchDate,
  getPreorderDispatchFallback,
} from "@/helpers/cartLineAttributes.js";
import PreorderDispatchLine from "@/components/product-display/PreorderDispatchLine.vue";
import ColourFamilySwatches from "@/components/product-display/ColourFamilySwatches.vue";
import LowStockMessage from "@/components/product-display/LowStockMessage.vue";
import { businessLogic } from "@/config.js";

// Import third parties
import { mapState, mapGetters, mapActions } from "vuex";
import SizeGuideSS25 from "@/components/product-display/SizeGuideSS25.vue";
import PreorderVariantModal from "@/components/product-display/PreorderVariantModal.vue";
import WaitlistDrawer from "@/components/product-display/WaitlistDrawer.vue";

export default {
  name: "ProductPageV3",
  components: {
    HowToWear,
    Variants,
    AddToBag,
    DynamicCheckoutButtons,
    StickyAddToBag,
    IconText,
    DeliveryEstimatorNoInput,
    SizeGuideSS25,
    PreorderVariantModal,
    WaitlistDrawer,
    PreorderDispatchLine,
    ColourFamilySwatches,
    LowStockMessage,
    BNPLInformation,
    Easysize,
    ProductMediaGallery,
    AccordionGroup,
    Accordion,
    ProductDetails,
    WishlistButton,
    ProductPrice,
    ShippingAndReturns,
    ProductSkeleton,
    Aftercare,
    FabricAndPrint,
    ProductRecommendationsToggle,
    CompleteTheSet,
    StockNotificationForm,
    PointsBanner,
  },
  inject: ["mq"],
  props: {
    productHandle: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      showStickyBar: false,
      nostoProductRecommendationsProduct: null,
      nostoProductRecommendationsProductId: null,
      referredPlacement: null,
      showStockNotification: false,
      trueToSizeIcon,
      sheernessIcon,
      exchangesIcon,
      oneSizeIcon,
    };
  },
  computed: {
    ...mapState("auth", ["customer"]),
    ...mapState("product", ["product"]),
    ...mapState("add-to-bag", ["compactStockNotifOpen"]),
    ...mapGetters("auth", ["isLoggedIn"]),
    ...mapGetters("app", ["currencyCode"]),
    ...mapGetters("product", [
      "variants",
      "sellingPlanAllocation",
      "availableForPreOrder",
      "variant",
      "selectedPrebuyCampaign",
      "prebuySellingPlanAllocation",
      "availableForPrebuy",
      "isPreLaunch",
    ]),
    isJewellery() {
      return this.product?.productType === "Jewellery";
    },
    isGiftCard() {
      const type = this.product?.productType?.toLowerCase?.() || "";
      const title = this.product?.title?.toLowerCase?.() || "";
      const handle = this.product?.handle?.toLowerCase?.() || "";

      return (
        type.includes("gift") ||
        title.includes("gift card") ||
        handle.includes("gift-card")
      );
    },
    expressCheckoutEnabled() {
      const { expressCheckoutVersion = 0 } = getFeatureToggles();
      return expressCheckoutVersion !== 0;
    },
    showStockNotificationForm() {
      // Waitlist A/B variants replace the inline compact form with the
      // WaitlistDrawer; pre-launch keeps the compact form in all arms.
      const { waitlistVersion = 0 } = getFeatureToggles();
      return (
        (this.compactStockNotifOpen && waitlistVersion === 0) ||
        this.isPreLaunch
      );
    },
    showFitQuizButton() {
      return this.product?.productType !== "Jewellery";
    },
    pointsBalance() {
      return parseFloat(this.customer?.approvedPoints?.value) || 0;
    },
    creditBalance() {
      if (!this.currencyCode) return;
      const value =
        this.pointsBalance / businessLogic.loyaltyPointsConversion || 0;

      return this.$filters.toCurrency(value, this.currencyCode, 0);
    },
    showPointsBanner() {
      const creditValue =
        this.pointsBalance / businessLogic.loyaltyPointsConversion || 0;
      const minimumValue = 0.99;

      const aboveMinimumValue = creditValue > minimumValue;

      return this.isLoggedIn && aboveMinimumValue;
    },
    isEDDEnabled() {
      if (this.currencyCode) {
        return regionFeatureToggles[this.currencyCode].eddEnabled || false;
      }
      return false;
    },
    showDeliveryEstimateComponent() {
      return this.isEDDEnabled;
    },
    productId() {
      return this.product?.id;
    },
    showVariants() {
      return this.variants.length > 1;
    },
    isOneSizeProduct() {
      return this.variants.length === 1 && !this.isGiftCard;
    },
    preorderDispatchDate() {
      if (this.availableForPrebuy && this.selectedPrebuyCampaign) {
        return getPreorderDispatchDate(
          this.selectedPrebuyCampaign.estDispatchDate
        );
      }

      const description = this.sellingPlanAllocation?.sellingPlan?.description;
      const match = description?.match(/Expected Dispatch:\s*(.+)/i);

      return match ? match[1].trim() : null;
    },
    preorderDispatchFallback() {
      if (this.preorderDispatchDate) return null;

      if (this.availableForPrebuy && this.selectedPrebuyCampaign) {
        return (
          this.selectedPrebuyCampaign.productPageText ||
          getPreorderDispatchFallback(
            this.prebuySellingPlanAllocation?.sellingPlan?.description
          )
        );
      }

      return getPreorderDispatchFallback(
        this.sellingPlanAllocation?.sellingPlan?.description
      );
    },
    showSellingPlanDescription() {
      if (!this.availableForPreOrder && !this.availableForPrebuy) {
        return false;
      }

      return !!(this.preorderDispatchDate || this.preorderDispatchFallback);
    },
    trueToSizeMessage() {
      const value = this.product?.trueToSize?.value;
      const sizeMessageMap = {
        0: "Runs small, we recommend sizing up from your usual size.",
        1: "Fits true to size.",
        2: "Runs large, we recommend sizing down from your usual size.",
      };

      return sizeMessageMap[String(value)] || "";
    },
    sheernessMessage() {
      const value =
        this.product?.sheerness?.value ||
        this.product?.sheernessTrailingUnderscore?.value ||
        this.product?.sheernessAlt?.value;
      const normalized = String(value || "")
        .toLowerCase()
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();

      const sheernessMessageMap = {
        sheer: "Sheer by design.",
        "semi sheer": "Semi sheer by design.",
        semisheer: "Semi sheer by design.",
        "non sheer": "Non sheer.",
        nonsheer: "Non sheer.",
      };

      if (sheernessMessageMap[normalized]) {
        return sheernessMessageMap[normalized];
      }

      if (normalized.includes("semi") && normalized.includes("sheer")) {
        return "Semi sheer by design.";
      }

      if (normalized.includes("non") && normalized.includes("sheer")) {
        return "Non sheer.";
      }

      if (normalized.includes("sheer")) {
        return "Sheer by design.";
      }

      return "";
    },
    // Set to true when exchanges/returns advisory should show on PDP again
    showAdvisoryReturnsMessage() {
      return false;
    },
    advisoryReturnsMessage() {
      return "Easy exchanges within 30 days. Free UK returns, simple online process.";
    },
  },
  watch: {
    loading(val) {
      if (!val) {
        this.$nextTick(() => this.setupStickyObserver());
      }
    },
    productId(val) {
      if (!val) {
        return;
      }

      const splitProductId = this.productId.split("Product/")[1];

      // eslint-disable-next-line no-undef
      nostojs((api) => {
        api
          .defaultSession()
          .viewProduct(val)
          .setRef(splitProductId, this.referredPlacement)
          .setPlacements(["productpage-nosto-1"])
          .load()
          .then((data) => {
            this.nostoProductRecommendationsProduct =
              data.recommendations["productpage-nosto-1"]?.products || null;
            this.nostoProductRecommendationsProductId =
              data.recommendations["productpage-nosto-1"]?.result_id || null;
          });
      });

      viewProductEvent(this.product);
      checkProductPages();

      this.setRecentlyViewedProducts(this.product.id);
    },
  },
  created() {
    this.params = getParams(window.location);
    this.referredPlacement = this.params["ref-placement"];
  },
  beforeUnmount() {
    if (this.stickyObserver) {
      this.stickyObserver.disconnect();
    }

    this.resetPreorderModalSession();
  },
  methods: {
    ...mapActions("app", ["setRecentlyViewedProducts"]),
    ...mapActions("product", [
      "setProduct",
      "setSelectedVariant",
      "selectVariantForPurchase",
      "setDefaultVariant",
      "setOpenSizeGuide",
      "resetPreorderModalSession",
    ]),
    openSizeGuide() {
      this.setOpenSizeGuide(true);
    },
    setupStickyObserver() {
      if (this.mq.lgPlus) return;

      const el = this.$refs.addToBag;
      if (!el) return;

      this.stickyObserver = new IntersectionObserver(
        ([entry]) => {
          this.showStickyBar =
            !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        },
        { threshold: 0 }
      );

      this.stickyObserver.observe(el);
    },
    scrollToVariants() {
      const variantsRef = this.$refs.variants;
      const ref =
        variantsRef || (this.isPreLaunch ? this.$refs.addToBag : null);
      const el = ref && (ref.$el || ref);

      if (!el) {
        return;
      }

      const rect = el.getBoundingClientRect();
      const absoluteTop = rect.top + window.scrollY;
      const headerOffset = 100;

      window.scrollTo({
        top: absoluteTop - headerOffset,
        behavior: "smooth",
      });
    },
  },
  apollo: {
    product: {
      query: productQuery,
      skip() {
        return !this.productHandle;
      },
      variables() {
        return {
          product: this.productHandle,
        };
      },
      result(resp) {
        const product = resp?.data?.product;

        this.setProduct(product);
        this.setDefaultVariant(product.variants.edges[0].node);

        // Restore the selected variant from the URL so the chosen size
        // persists across reload and back-navigation.
        const variantId = getParams(window.location).variant;
        const preSelectedVariant = variantId
          ? product.variants.edges.find(
              ({ node }) =>
                node.id === `gid://shopify/ProductVariant/${variantId}`
            )
          : null;

        if (preSelectedVariant) {
          this.setSelectedVariant(preSelectedVariant.node);
        } else {
          this.setSelectedVariant(null);
        }

        // For single-sized products, auto-select the only variant so
        // the button shows "ADD TO BAG" instead of "SELECT SIZE".
        if (product.variants.edges.length === 1) {
          this.selectVariantForPurchase(product.variants.edges[0].node);
        } else if (
          product.tags?.includes("pre-launch") &&
          !preSelectedVariant
        ) {
          this.selectVariantForPurchase(product.variants.edges[0].node);
        }

        this.loading = false;
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.product-grid {
  @include respond-above(lg) {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 427px;
    column-gap: 40px;
    padding: 40px 40px 60px;
  }
}

.product {
  border-top: 1px solid $color-grey-light;

  &__media {
    position: relative;
  }

  &__title {
    @include editorial-headline(20px);
    color: $color-black;

    @include respond-above(lg) {
      @include editorial-headline(20px);
      margin: 0;
    }
  }

  &__title-wrapper {
    margin-bottom: 10px;

    @include respond-above(lg) {
      margin-bottom: 20px;
    }
  }

  &__title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  &__info {
    position: relative;

    @include respond-above(lg) {
      align-self: start;
    }

    .icon-text:not(:last-child) {
      margin: 0 0 8px 0;
    }
  }

  &__info-inner {
    padding: 20px 20px 60px 20px;

    @include respond-above(md) {
      padding: 20px 160px 60px 160px;
    }

    @include respond-above(lg) {
      padding: 0;
      max-width: 427px;
    }
  }

  &__info-narrow {
    padding-right: 50px;
  }

  &__price {
    @include utility-body(14px);
    margin: 0;
  }

  &__bnpl {
    margin-top: 20px;
    padding: 20px 0;
    border-top: 1px solid $color-grey-light;
    border-bottom: 1px solid $color-grey-light;

    :deep(.provider, .text) {
      margin: 0;
      @include editorial-body(14px);
      line-height: unset;
      color: $color-grey-dark;
    }
  }

  &__sticky-cta {
    z-index: 2;
    background: $color-white;
    margin-bottom: 0;
  }

  &__payment {
    margin-bottom: 0;
  }

  &__accordion {
    margin-bottom: 20px;

    > .accordion:last-of-type {
      border-bottom: 1px solid $color-grey-light;
    }

    :deep(.accordion__header) {
      @include editorial-body(14px);
    }

    :deep(.accordion__body-inner),
    :deep(.accordion__body-inner .wysiwyg),
    :deep(.accordion__body-inner .wysiwyg p),
    :deep(.accordion__body-inner p),
    :deep(.accordion__body-inner li),
    :deep(.accordion__body-inner .shipping__estimated-delivery),
    :deep(.accordion__body-inner .shipping__list),
    :deep(.accordion__body-inner .shipping__list__item),
    :deep(.accordion__body-inner .aftercare__list),
    :deep(.accordion__body-inner .aftercare__list li) {
      @include editorial-body(14px);
    }
  }

  &__fabric-feel-link {
    @include editorial-body(14px);
    display: inline-block;
    margin-top: 16px;
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  &__fit-size-copy {
    @include editorial-body(14px);
    margin: 0 0 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__fit-size-links {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  &__fit-size-link {
    @include editorial-body(14px);
    margin: 0;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    font-family: inherit;
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
    text-align: left;

    &:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
    }
  }

  &__fit-size-easysize {
    :deep(.easysize_placeholder) {
      display: none;
    }

    :deep(.text-wrapper.text-wrapper--compact) {
      align-items: flex-start;
    }

    :deep(.text-wrapper .text:empty) {
      display: none;
    }

    :deep(.button) {
      @include editorial-body(14px);
      text-transform: none;
      padding: 0;

      &.button--underline .text::after {
        background: currentColor;
        bottom: -2px;
        height: 1px;
      }
    }
  }

  &__advisory {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 16px 0 20px;
  }

  &__advisory-item {
    background: #f5f5f5;
    border-radius: 4px;
    padding: 16px 20px;
    display: flex;
    align-items: center;
    gap: 20px;

    p {
      @include editorial-body(14px);
      font-style: italic;
      margin: 0;
    }
  }

  &__advisory-icon {
    width: 20px;
    height: 20px;
    display: block;
    flex: 0 0 20px;
  }
}

.product__payment-row {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.product__payment-row :deep(.add-to-bag) {
  flex: 1 1 auto;
}

.product__payment-row :deep(.add-to-bag .button) {
  width: 100%;
}

.product__payment-row :deep(.dynamic-checkout-buttons) {
  width: 100%;
}

.variants-component {
  margin-bottom: 20px;
  @include respond-above(mg) {
    margin-bottom: 40px;
  }
}

:deep(.variants__wrapper) {
  .variants {
    flex-wrap: wrap;
  }

  .variant {
    flex: 0 0 calc((100% - 18px) / 4);
    max-width: calc((100% - 18px) / 4);

    @include respond-above(lg) {
      flex: 0 0 calc((100% - 39px) / 4);
      max-width: calc((100% - 39px) / 4);
    }
  }

  .variants-meta {
    order: -1;
    justify-content: flex-end;
    margin: 0 0 16px;
    gap: 8px;
  }

  .variants-meta-item--center {
    justify-content: flex-end;
  }

  .variants-button,
  .variants-fit-quiz .button {
    @include editorial-body(14px);
    font-style: italic;
    color: $color-black;
  }

  .variants-button .text,
  .variants-fit-quiz .text {
    color: $color-black;
  }

  .variants-button .text::after,
  .variants-fit-quiz .button--underline .text::after {
    @include editorial-dotted-underline($color-black);
  }
}

.product__title-bookmark {
  :deep(.wishlist-inline) {
    background: transparent;
    border-radius: 0;
    padding: 0;
  }

  :deep(.wishlist-inline__divider) {
    display: none;
  }

  :deep(.wishlist-inline__icon) {
    width: 12px;
    height: 16px;
  }
}

.grid {
  margin-top: 20px;
  @include respond-above(mg) {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    margin-top: 40px;
  }

  .main {
    flex: 1 1 auto;
  }
}

.wishlist-button {
  position: absolute;
  top: 10px;
  right: 10px;

  @include respond-above(lg) {
    position: static;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  transform: translateY(100%);
}

:deep(.wysiwyg > p) {
  @include editorial-body(14px);
}

:deep {
  .read-more {
    @include editorial-body(14px);

    &__toggle {
      text-decoration: underline dotted;
      text-underline-offset: 3px;
    }

    .bullets {
      margin-left: 4px;
    }

    .bullets__bullet {
      @include editorial-body(14px);
    }
  }
}

.icon-text {
  :deep(.text) {
    @include utility-label(15px);
    text-transform: none;
  }

  :deep(.icon) {
    font-size: 20px;
    height: 20px;
    width: 20px;
  }
}

.points-banner {
  background-color: #fafafa;
  padding: 20px 0;
  text-align: center;
  @include utility-body(15px);
}
</style>
